import {
  Dropdown,
  DropdownItem,
  DropdownItemContent,
  IconButton,
  Tag,
} from '@ads/components-react';
import {
  EllipsisVerticalIcon,
  EyeIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import type { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ExamGridListItemViewModel } from '@/shared/api/generated/types/ExamGridListItemViewModel';
import { DataTable } from '@/shared/ui/organisms/DataTable';

const PAGE_SIZE = 20;

export interface ExamTableProps {
  readonly data: readonly ExamGridListItemViewModel[];
  readonly isLoading: boolean;
  readonly totalCount: number;
  readonly currentPage: number;
  readonly totalPages: number;
  readonly onPageChange: (page: number) => void;
  readonly onView: (id: number) => void;
  readonly onEdit: (id: number) => void;
  readonly onDelete: (id: number) => void;
}

/**
 * Tabela de avaliações — título, tipo, disciplina, status (Tag) e ações
 * (visualizar/editar/excluir), delegando loading/empty/paginação ao `DataTable`
 * para manter o mesmo padrão visual das demais tabelas do projeto.
 */
export function ExamTable({
  data,
  isLoading,
  totalCount,
  currentPage,
  totalPages,
  onPageChange,
  onView,
  onEdit,
  onDelete,
}: ExamTableProps) {
  const { t } = useTranslation('examManagement');

  const columns = useMemo<ColumnDef<ExamGridListItemViewModel, unknown>[]>(
    () => [
      {
        accessorKey: 'title',
        header: t('table.columns.title'),
        cell: info => (
          <span className="ts-paragraph-xxs text-ctx-content-base">
            {(info.getValue() as string) ?? '-'}
          </span>
        ),
      },
      {
        id: 'examTypeText',
        accessorKey: 'examTypeText',
        header: t('table.columns.type'),
        cell: ({ row }) => (
          <span className="ts-paragraph-xxs text-ctx-content-base">
            {row.original.examTypeText || '-'}
          </span>
        ),
      },
      {
        id: 'disciplineText',
        accessorKey: 'disciplineText',
        header: t('table.columns.discipline'),
        cell: ({ row }) => (
          <span className="ts-paragraph-xxs text-ctx-content-base">
            {row.original.disciplineText || '-'}
          </span>
        ),
      },
      {
        id: 'status',
        header: t('table.columns.status'),
        cell: ({ row }) => (
          <Tag
            label={row.original.active ? t('status.active') : t('status.inactive')}
            size="sm"
            status={row.original.active ? 'positive' : 'default'}
            custom={{ text: 'text-nowrap' }}
          />
        ),
      },
      {
        id: 'actions',
        header: t('table.columns.actions'),
        cell: ({ row }) => {
          const id = row.original.id ?? 0;
          return (
            <div className="flex justify-end">
              <Dropdown
                trigger={
                  <IconButton
                    icon={EllipsisVerticalIcon}
                    ariaLabel={t('table.columns.actions')}
                    hierarchy="tertiary"
                    size="md"
                  />
                }
                align="end"
              >
                <DropdownItem onClick={() => onView(id)}>
                  <DropdownItemContent leadingIcon={EyeIcon}>
                    {t('actions.view')}
                  </DropdownItemContent>
                </DropdownItem>
                <DropdownItem onClick={() => onEdit(id)}>
                  <DropdownItemContent leadingIcon={PencilSquareIcon}>
                    {t('actions.edit')}
                  </DropdownItemContent>
                </DropdownItem>
                <DropdownItem status="destructive" onClick={() => onDelete(id)}>
                  <DropdownItemContent leadingIcon={TrashIcon}>
                    {t('actions.delete')}
                  </DropdownItemContent>
                </DropdownItem>
              </Dropdown>
            </div>
          );
        },
      },
    ],
    [t, onView, onEdit, onDelete],
  );

  return (
    <DataTable
      data={data as ExamGridListItemViewModel[]}
      columns={columns}
      isLoading={isLoading}
      totalCount={totalCount}
      currentPage={currentPage}
      totalPages={totalPages}
      pageSize={PAGE_SIZE}
      onPageChange={onPageChange}
      emptyStateMessage={t('table.empty')}
    />
  );
}
